// ============================================================================
// Entity: RuleSets
// Key: ruleSets
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	getCustomPropertiesListQueryKey,
	getRuleSetsListQueryKey,
	getRuleSetsListQueryOptions,
	getRuleSetsRelationsCustomPropertiesAssignedQueryKey,
	getRuleSetsRelationsCustomPropertiesAssignedQueryOptions,
	getRuleSetsRelationsCustomPropertiesAvailableQueryKey,
	getRuleSetsRelationsCustomPropertiesAvailableQueryOptions,
	ruleSetsCreate,
	ruleSetsDelete,
	ruleSetsRelationsCustomPropertiesAssign,
	ruleSetsRelationsCustomPropertiesUnassign,
	ruleSetsUpdate,
} from '../api/backstage'
// #region Imports
import type {
	CreateOrUpdateRuleSetRequest,
	CustomPropertyRuleSetDto,
	PagedResultOfCustomPropertyRuleSetDto,
	PagedResultOfCustomPropertySummary,
	PagedResultOfRuleSetSummary,
	RuleSetSummary,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const ruleSetsEntityEndpoints = {
	create: defineMutationEndpoint<
		RuleSetSummary,
		CreateOrUpdateRuleSetRequest,
		undefined
	>({
		key: 'ruleSets.create',
		method: 'POST',
		path: '/admin/rulesets',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateOrUpdateRuleSetRequest }) =>
				ruleSetsCreate(variables.data),
		}),
		invalidates: [(_context) => getRuleSetsListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { ruleSetId: number }>({
		key: 'ruleSets.delete',
		method: 'DELETE',
		path: '/admin/rulesets/{ruleSetId}',
		pathParams: ['ruleSetId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				ruleSetId: number | string | null | undefined
			}) => ruleSetsDelete(variables.ruleSetId),
		}),
		invalidates: [(_context) => getRuleSetsListQueryKey()],
	}),
	list: defineQueryEndpoint<
		PagedResultOfRuleSetSummary,
		undefined,
		ListQueryParams
	>({
		key: 'ruleSets.list',
		method: 'GET',
		path: '/admin/rulesets',
		queryKey: (args) => getRuleSetsListQueryKey(args?.queryParams),
		queryOptions: (args) => getRuleSetsListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		RuleSetSummary,
		CreateOrUpdateRuleSetRequest,
		{ ruleSetId: number }
	>({
		key: 'ruleSets.update',
		method: 'PUT',
		path: '/admin/rulesets/{ruleSetId}',
		pathParams: ['ruleSetId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				ruleSetId: number | string | null | undefined
				data: CreateOrUpdateRuleSetRequest
			}) => ruleSetsUpdate(variables.ruleSetId, variables.data),
		}),
		invalidates: [(_context) => getRuleSetsListQueryKey()],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: CustomProperties
// Target Entity: customProperties
const ruleSetsCustomPropertiesRelation = {
	key: 'customProperties',
	targetEntity: 'customProperties',
	endpoints: {
		assign: defineMutationEndpoint<
			CustomPropertyRuleSetDto[],
			(number | string)[],
			{ ruleSetId: number }
		>({
			key: 'ruleSets.relations.customProperties.assign',
			method: 'POST',
			path: '/admin/rulesets/{ruleSetId}/customproperties',
			pathParams: ['ruleSetId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					ruleSetId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					ruleSetsRelationsCustomPropertiesAssign(
						variables.ruleSetId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getRuleSetsRelationsCustomPropertiesAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getRuleSetsRelationsCustomPropertiesAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getRuleSetsListQueryKey(),
				(_context) => getCustomPropertiesListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfCustomPropertyRuleSetDto,
			{ ruleSetId: number },
			ListQueryParams
		>({
			key: 'ruleSets.relations.customProperties.assigned',
			method: 'GET',
			path: '/admin/rulesets/{ruleSetId}/customproperties',
			pathParams: ['ruleSetId'] as const,
			queryKey: (args) =>
				getRuleSetsRelationsCustomPropertiesAssignedQueryKey(
					args?.pathParams?.ruleSetId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getRuleSetsRelationsCustomPropertiesAssignedQueryOptions(
					args?.pathParams?.ruleSetId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfCustomPropertySummary,
			{ ruleSetId: number },
			ListQueryParams
		>({
			key: 'ruleSets.relations.customProperties.available',
			method: 'GET',
			path: '/admin/rulesets/{ruleSetId}/assignable-customproperties',
			pathParams: ['ruleSetId'] as const,
			queryKey: (args) =>
				getRuleSetsRelationsCustomPropertiesAvailableQueryKey(
					args?.pathParams?.ruleSetId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getRuleSetsRelationsCustomPropertiesAvailableQueryOptions(
					args?.pathParams?.ruleSetId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ ruleSetId: number }
		>({
			key: 'ruleSets.relations.customProperties.unassign',
			method: 'DELETE',
			path: '/admin/rulesets/{ruleSetId}/customproperties',
			pathParams: ['ruleSetId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					ruleSetId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					ruleSetsRelationsCustomPropertiesUnassign(
						variables.ruleSetId,
						variables.data,
					),
			}),
			invalidates: [
				(context) =>
					getRuleSetsRelationsCustomPropertiesAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getRuleSetsRelationsCustomPropertiesAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getRuleSetsListQueryKey(),
				(_context) => getCustomPropertiesListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Entity Config Export
export const ruleSetsEntityConfig = {
	key: 'ruleSets',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'ruleSetId',
		routePath: '/ruleSets',
	},
	endpoints: ruleSetsEntityEndpoints,
	relations: {
		customProperties: ruleSetsCustomPropertiesRelation,
	},
} as const satisfies EntityConfig<'ruleSets'>
// #endregion
